/**
 * The single owner of browser storage for the SDK and its host apps.
 *
 * Nothing outside this module should touch window.localStorage, window.sessionStorage, or
 * document.cookie for session data. Two reasons, both load-bearing:
 *
 *  1. Which storage area a session uses depends on its LocalDataEncryptionMode, and that decision
 *     must be made in one place rather than re-derived at every call site.
 *  2. Logout has to remove *everything* an account wrote. That is only possible if every key is
 *     built to a known prefix, which is only true if every write goes through here.
 */

import { LocalDataEncryptionMode } from "../constants/Enums";
import {
  REGISTRY_KEY,
  deviceKey,
  flowKey,
  flowPrefix,
  sessionKey,
  sessionPrefix,
  tabKey,
} from "./StorageKeys";

/**
 * The mode arrives from callers as a number, and previously round-tripped through a cookie as a
 * string. Normalise once so "1" and 1 cannot select different storage areas.
 */
export const isExplicitLogoutMode = (mode) =>
  Number(mode) === LocalDataEncryptionMode.EXPLICIT_LOGOUT_MODE;

/**
 * Storage areas are read lazily rather than at module load: the SDK is imported in environments
 * (tests, SSR bundlers) where window does not exist yet.
 */
const localArea = () => window.localStorage;
const sessionArea = () => window.sessionStorage;

/** The area a session's own state lives in, chosen by its mode. */
const areaForMode = (mode) => (isExplicitLogoutMode(mode) ? localArea() : sessionArea());

/** Both areas, for reads and deletions that must not depend on knowing the mode. */
const bothAreas = () => [localArea(), sessionArea()];

export const readSessionValue = (sessionId, identifier, mode) =>
  areaForMode(mode).getItem(sessionKey(sessionId, identifier));

export const writeSessionValue = (sessionId, identifier, value, mode) => {
  areaForMode(mode).setItem(sessionKey(sessionId, identifier), value);
};

export const removeSessionValue = (sessionId, identifier, mode) => {
  areaForMode(mode).removeItem(sessionKey(sessionId, identifier));
};

/**
 * Reads a session value without being told the mode, by checking both areas.
 *
 * Used on paths that run before the registry has a record for the session — recovery, and the
 * migration in readLegacyState. Ordinary reads should pass the mode.
 */
export const readSessionValueAnyArea = (sessionId, identifier) => {
  const key = sessionKey(sessionId, identifier);
  for (const area of bothAreas()) {
    const value = area.getItem(key);
    if (value != null) {
      return value;
    }
  }
  return null;
};

export const readDeviceValue = (identifier) => localArea().getItem(deviceKey(identifier));

export const writeDeviceValue = (identifier, value) => {
  localArea().setItem(deviceKey(identifier), value);
};

export const removeDeviceValue = (identifier) => {
  localArea().removeItem(deviceKey(identifier));
};

/** Flow values are per-tab by construction, so they always live in sessionStorage. */
export const readFlowValue = (flowId, identifier) =>
  sessionArea().getItem(flowKey(flowId, identifier));

export const writeFlowValue = (flowId, identifier, value) => {
  sessionArea().setItem(flowKey(flowId, identifier), value);
};

const removeByPrefix = (area, prefix) => {
  // Collect first: removing while iterating by index re-indexes the area underneath the loop and
  // silently skips keys.
  const doomed = [];
  for (let i = 0; i < area.length; i += 1) {
    const key = area.key(i);
    if (key && key.startsWith(prefix)) {
      doomed.push(key);
    }
  }
  doomed.forEach((key) => area.removeItem(key));
  return doomed.length;
};

/**
 * Removes every key belonging to a session, in both storage areas.
 *
 * A prefix scan rather than a list of known keys, because host apps write session-scoped keys the
 * SDK has never heard of. Listing them was how logout came to leave data behind.
 *
 * @returns {Number} how many keys were removed, for logging and tests.
 */
export const clearSessionScope = (sessionId) => {
  const prefix = sessionPrefix(sessionId);
  return bothAreas().reduce((total, area) => total + removeByPrefix(area, prefix), 0);
};

export const clearFlowScope = (flowId) => removeByPrefix(sessionArea(), flowPrefix(flowId));

/** Tab-scoped values always live in sessionStorage; that is what makes them per-tab. */
export const readTabValue = (identifier) => sessionArea().getItem(tabKey(identifier));

export const writeTabValue = (identifier, value) => {
  sessionArea().setItem(tabKey(identifier), value);
};

export const removeTabValue = (identifier) => {
  sessionArea().removeItem(tabKey(identifier));
};

/** Raw registry access. Shaped and validated by SessionRegistry, which is the only caller. */
export const readRegistryRaw = () => localArea().getItem(REGISTRY_KEY);

export const writeRegistryRaw = (value) => {
  localArea().setItem(REGISTRY_KEY, value);
};

export const removeRegistryRaw = () => {
  localArea().removeItem(REGISTRY_KEY);
};
